import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, Thermometer, MapPin, Lock, FileCheck, Bell,
  ArrowRight, Check, ChevronRight, Pill, Truck,
  Cpu, Database, Eye, CheckCircle,
  Snowflake, Syringe, TestTube, Building2, Award, Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Thermometer,
    title: 'Cold Chain Compliance',
    description: '2-8°C temperature monitoring with real-time alerts for vaccines, biologics, and temperature-sensitive medications.',
    highlight: 'GDP Compliant'
  },
  {
    icon: MapPin,
    title: 'Real-time GPS Tracking',
    description: 'Live location updates with geofencing, route deviation alerts, and accurate delivery ETAs.',
    highlight: 'Live Updates'
  },
  {
    icon: Lock,
    title: 'Tamper-proof Security',
    description: 'Electronic seals with instant breach detection and complete chain of custody documentation.',
    highlight: 'Audit Ready'
  },
  {
    icon: FileCheck,
    title: 'Regulatory Documentation',
    description: 'Automatic generation of compliance reports for Drugs & Cosmetics Act and GDP requirements.',
    highlight: 'Auto Reports'
  },
  {
    icon: Bell,
    title: 'Smart Alert System',
    description: 'Configurable notifications for temperature excursions, delivery delays, and security events.',
    highlight: '24/7 Alerts'
  },
  {
    icon: Database,
    title: 'Digital Audit Trail',
    description: 'Immutable logs of every touchpoint from manufacturer to patient for complete traceability.',
    highlight: 'Full History'
  }
];

const benefits = [
  { metric: '99.9%', label: 'Cold Chain Compliance', icon: Thermometer },
  { metric: '40%', label: 'Reduction in Spoilage', icon: Pill },
  { metric: '100%', label: 'Audit Traceability', icon: FileCheck },
  { metric: '24/7', label: 'Real-time Monitoring', icon: Eye }
];

const compliance = [
  { name: 'Drugs & Cosmetics Act, 1940', icon: FileCheck },
  { name: 'D&C Rules, 1945', icon: FileCheck },
  { name: 'Good Distribution Practices (GDP)', icon: Award },
  { name: 'WHO Cold Chain Guidelines', icon: Snowflake },
  { name: 'Schedule M Compliance', icon: Building2 },
  { name: 'State Drug Licensing', icon: Shield }
];

const useCases = [
  {
    title: 'Vaccine Distribution',
    description: 'End-to-end cold chain visibility for COVID-19, routine immunization, and specialty vaccines.',
    icon: Syringe,
    image: 'https://images.unsplash.com/photo-1639772823849-6efbd173043c?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Biologics & Biosimilars',
    description: 'Temperature-controlled transport for insulin, growth hormones, and monoclonal antibodies.',
    icon: TestTube,
    image: 'https://images.pexels.com/photos/7230183/pexels-photo-7230183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    title: 'Hospital & Clinic Supply',
    description: 'Last-mile delivery assurance for healthcare facilities with proof of compliance.',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1732690233982-1d4567384ea1?crop=entropy&cs=srgb&fm=jpg&q=85'
  }
];

export default function TrackOticPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '', email: '', company: '', phone: '', message: ''
  });

  const handleSubmit = async () => {
    if (!contactForm.name || !contactForm.email) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        service_interest: 'TrackOtic',
        message: `TrackOtic Inquiry: ${contactForm.message}`
      });
      toast.success('Request submitted! Our team will contact you shortly.');
      setContactOpen(false);
      setContactForm({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  return (
    <div data-testid="trackotic-page" className="min-h-screen pt-16 bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden gradient-hero">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200 font-medium">
                DELIVERY ASSURANCE PLATFORM
              </Badge>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
                Track<span className="text-primary">Otic</span>
                <span className="text-2xl align-top text-gray-400">™</span>
              </h1>
              
              <p className="text-xl text-blue-600 font-medium mb-4">
                Cold Chain Compliance Platform for Pharmaceuticals
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl">
                A comprehensive technology-led platform ensuring secure, compliant, and 
                auditable delivery of pharmaceuticals with real-time IoT monitoring and 
                regulatory-grade documentation.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/app/login">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-blue-700 text-white font-semibold h-14 px-8 rounded-lg shadow-primary"
                    data-testid="launch-platform-btn"
                  >
                    Launch Platform
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-gray-300 hover:border-primary font-semibold h-14 px-8 rounded-lg"
                      data-testid="request-demo-btn"
                    >
                      Request Demo
                      <Phone className="ml-2 h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Request TrackOtic Demo</DialogTitle>
                      <DialogDescription>
                        Our team will schedule a personalized demo for your organization.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input 
                            placeholder="Your name"
                            value={contactForm.name}
                            onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                            data-testid="demo-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input 
                            placeholder="+91 98765 43210"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input 
                          type="email"
                          placeholder="you@company.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                          data-testid="demo-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company / Organization</Label>
                        <Input 
                          placeholder="Your organization"
                          value={contactForm.company}
                          onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Message</Label>
                        <Textarea 
                          placeholder="Tell us about your distribution requirements..."
                          value={contactForm.message}
                          onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                        />
                      </div>
                      <Button onClick={handleSubmit} className="w-full" data-testid="submit-demo-btn">
                        Submit Request
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Link to="/contact">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-2 border-primary text-primary hover:bg-primary hover:text-white h-14 px-8 rounded-lg"
                    data-testid="contact-sales-btn"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Sales
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  GDP Compliant
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  D&C Act Ready
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  WHO Guidelines
                </span>
              </div>
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-gray-900">TrackOtic Dashboard</div>
                      <div className="text-xs text-gray-500">Pharma Distribution</div>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    All Systems Normal
                  </Badge>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="text-3xl font-bold text-primary">127</div>
                    <div className="text-sm text-gray-600">Active Shipments</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4">
                    <div className="text-3xl font-bold text-green-600">99.8%</div>
                    <div className="text-sm text-gray-600">Compliance Rate</div>
                  </div>
                </div>

                {/* Temperature Status */}
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">Cold Chain Status</span>
                    <Badge className="bg-green-100 text-green-700">Optimal</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <Thermometer className="w-8 h-8 text-blue-500" />
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Temperature Range</span>
                        <span className="font-mono text-green-600">4.2°C</span>
                      </div>
                      <Progress value={70} className="h-2" />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>2°C</span>
                        <span>8°C</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="space-y-2">
                  {[
                    { time: '2m ago', event: 'Vaccine batch delivered', status: 'success' },
                    { time: '15m ago', event: 'Temperature verified', status: 'success' },
                    { time: '1h ago', event: 'New shipment dispatched', status: 'info' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50">
                      <div className={`w-2 h-2 rounded-full ${
                        item.status === 'success' ? 'bg-green-500' : 'bg-blue-500'
                      }`} />
                      <span className="text-gray-500 w-16">{item.time}</span>
                      <span className="text-gray-700">{item.event}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Stats */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-1">{benefit.metric}</div>
                <div className="text-sm text-gray-600">{benefit.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              PLATFORM FEATURES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for Pharmaceutical Distribution
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Every feature designed to meet the unique challenges of pharmaceutical cold chain logistics
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow"
                data-testid={`feature-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <Badge className="bg-blue-50 text-blue-600 border-blue-100 text-xs">
                    {feature.highlight}
                  </Badge>
                </div>
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              USE CASES
            </Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pharmaceutical Applications
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Trusted by pharma distributors, hospitals, and healthcare facilities
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-soft border border-gray-100 hover:shadow-lg transition-shadow group"
                data-testid={`usecase-${index}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 bg-white/90 rounded-lg flex items-center justify-center">
                      <useCase.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-gray-600 text-sm">{useCase.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-blue-50" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
                REGULATORY COMPLIANCE
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Built for Indian
                <span className="text-primary"> Pharma Regulations</span>
              </h2>
              <p className="text-gray-600 mb-8">
                TrackOtic is architected to comply with the Drugs & Cosmetics Act 1940, 
                D&C Rules 1945, and Good Distribution Practices (GDP), ensuring your 
                pharmaceutical distribution meets all regulatory requirements.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {compliance.map((item) => (
                  <div key={item.name} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                    <item.icon className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-gray-700">{item.name}</span>
                  </div>
                ))}
              </div>

              <Button 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-white"
                onClick={() => setContactOpen(true)}
              >
                Download Compliance Guide
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="text-7xl font-bold text-primary mb-2">99.8%</div>
                <div className="text-gray-600 mb-8">Compliance Rate</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <FileCheck className="w-8 h-8 text-primary mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Audit Ready</div>
                    <div className="text-xs text-gray-500">Digital documentation</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <Database className="w-8 h-8 text-primary mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Full Traceability</div>
                    <div className="text-xs text-gray-500">Immutable logs</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Pharma Distribution?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Join leading pharmaceutical distributors who trust TrackOtic for 
              compliance-grade cold chain management.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-primary hover:bg-gray-100 font-semibold h-14 px-8 rounded-lg"
                onClick={() => setContactOpen(true)}
                data-testid="cta-demo-btn"
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-primary h-14 px-8 rounded-lg"
                  data-testid="cta-contact-btn"
                >
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
